<template>
  <section v-if="!shouldHideCard" class="rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-800 dark:bg-amber-950/20">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="text-base font-medium text-amber-950 dark:text-amber-100">
            {{ title }}
          </h3>
          <Badge :variant="statusBadgeVariant" class="shrink-0 bg-background/80">
            {{ statusBadgeLabel }}
          </Badge>
        </div>

        <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-amber-900/80 dark:text-amber-100/80">
          <span>{{ helperText }}</span>
          <template v-if="localReviewProcess">
            <span class="text-amber-900/35 dark:text-amber-100/35">/</span>
            <span><span class="font-medium text-amber-950 dark:text-amber-100">Final approver:</span> {{ finalApproverDisplay }}</span>
            <template v-if="submittedByDisplay !== null">
              <span class="text-amber-900/35 dark:text-amber-100/35">/</span>
              <span><span class="font-medium text-amber-950 dark:text-amber-100">Submitted:</span> {{ submittedByDisplay }}</span>
            </template>
            <template v-if="finalDecisionByDisplay !== null">
              <span class="text-amber-900/35 dark:text-amber-100/35">/</span>
              <span><span class="font-medium text-amber-950 dark:text-amber-100">Decision:</span> {{ finalDecisionByDisplay }}</span>
            </template>
            <template v-if="finalDecisionAtDisplay !== null">
              <span class="text-amber-900/35 dark:text-amber-100/35">/</span>
              <span>{{ finalDecisionAtDisplay }}</span>
            </template>
            <template v-if="showCompactStateDetails">
              <span class="text-amber-900/35 dark:text-amber-100/35">/</span>
              <span>{{ compactSummaryText }}</span>
            </template>
          </template>
        </div>
      </div>

      <div v-if="showActionControls" class="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
        <Button
          v-if="primaryReviewAction"
          type="button"
          size="sm"
          :variant="primaryReviewAction.variant"
          :loading="isUpdatingStatus"
          :disabled="isUpdatingStatus || isRejectModeOpen"
          @click="runAction(primaryReviewAction)"
        >
          {{ primaryReviewAction.label }}
        </Button>

        <DropdownMenu v-if="hasReviewMenuActions">
          <DropdownMenuTrigger as-child>
            <Button type="button" size="icon" variant="outline" class="size-8 border-amber-300 bg-background/80 text-amber-800 hover:bg-background dark:border-amber-700 dark:text-amber-100" :disabled="isUpdatingStatus || isUpdatingFinalApprover">
              <SlidersHorizontal class="size-4" />
              <span class="sr-only">Review actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-56">
            <DropdownMenuItem v-if="canUpdateFinalApprover" :disabled="isUpdatingFinalApprover" @select="openFinalApproverModal">
              Change Final Approver
            </DropdownMenuItem>
            <DropdownMenuSeparator v-if="canUpdateFinalApprover && reviewMenuActions.length > 0" />
            <DropdownMenuItem
              v-for="action in reviewMenuActions"
              :key="`review-process-menu-action-${action.to}`"
              :disabled="isUpdatingStatus || isRejectModeOpen"
              :class="action.variant === 'destructive' ? 'text-destructive focus:text-destructive' : ''"
              @select="runAction(action)"
            >
              {{ action.label }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <p v-if="isLoading && !localReviewProcess" class="mt-3 text-sm text-muted-foreground">
      Loading review process...
    </p>

    <p v-else-if="errorMessage && !localReviewProcess" class="mt-3 text-sm text-destructive">
      {{ errorMessage }}
    </p>

    <template v-else-if="localReviewProcess">
      <div v-if="showRejectedReason" class="mt-3 rounded-md border bg-destructive/5 p-3 text-sm">
        <p class="font-medium text-destructive">
          Rejection reason
        </p>
        <p class="mt-1 text-muted-foreground">
          {{ rejectedReason }}
        </p>
      </div>

      <div v-if="showRejectReasonInput" class="mt-3 flex flex-col gap-3 rounded-md border bg-background/80 p-3">
        <Field>
          <FieldLabel for="review-process-rejection-reason">
            Rejection reason
          </FieldLabel>
          <Textarea
            id="review-process-rejection-reason"
            v-model="rejectionReason"
            rows="3"
            maxlength="1000"
            class="min-h-24"
            placeholder="Explain why this review is rejected"
          />
          <FieldDescription>
            This message is required and visible to the submitter.
          </FieldDescription>
        </Field>

        <div class="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="destructive"
            :loading="isUpdatingStatus"
            :disabled="isUpdatingStatus"
            @click="confirmRejectTransition"
          >
            Confirm Reject
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            :disabled="isUpdatingStatus"
            @click="cancelRejectTransition"
          >
            Cancel
          </Button>
        </div>
      </div>

      <p v-else-if="isFullMode && !showRejectReasonInput && !showActionControls" class="mt-3 text-sm text-muted-foreground">
        No actions are available for your role in the current state.
      </p>
    </template>

    <ReviewProcessFinalApproverModal
      ref="finalApproverModal"
      :selected-final-approver-id="selectedFinalApproverId"
      :is-loading-users="isLoadingUsers"
      :is-updating="isUpdatingFinalApprover"
      :can-submit="canSubmitFinalApproverChange"
      :users="users"
      @show="onFinalApproverModalShow"
      @submit="submitFinalApproverChange"
      @update:selected-final-approver-id="onSelectedFinalApproverIdUpdated"
    />
  </section>
</template>

<script setup>
import axios from 'axios'
import { SlidersHorizontal } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Textarea } from '../../../../../../../resources/js/components/ui/textarea/index.js'
import ReviewProcessFinalApproverModal from './ReviewProcessFinalApproverModal.vue'

const STATUS_LABELS = {
  not_submitted: 'Not Submitted',
  submitted_for_final_approval: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
}

const STATUS_HELPERS = {
  not_submitted: 'Select a final approver and submit when the item is ready.',
  submitted_for_final_approval: 'Waiting for final approver decision.',
  approved: 'Review process has been approved.',
  rejected: 'Review process was rejected and requires rework.',
  withdrawn: 'Review request was withdrawn.',
  cancelled: 'Review process was cancelled.',
}

const STATUS_BADGE_VARIANTS = {
  not_submitted: 'secondary',
  submitted_for_final_approval: 'default',
  approved: 'default',
  rejected: 'destructive',
  withdrawn: 'outline',
  cancelled: 'outline',
}

const STATUS_ACTION_UI = {
  not_submitted: {
    submitted_for_final_approval: { label: 'Submit for Approval', variant: 'default' },
    cancelled: { label: 'Cancel Review', variant: 'destructive', confirmMessage: 'Cancel this review process?' },
  },
  submitted_for_final_approval: {
    approved: { label: 'Approve', variant: 'default' },
    rejected: { label: 'Reject', variant: 'destructive', requiresReason: true },
    withdrawn: { label: 'Withdraw', variant: 'secondary', confirmMessage: 'Withdraw this review request?' },
    cancelled: { label: 'Cancel Review', variant: 'destructive', confirmMessage: 'Cancel this review process?' },
  },
  rejected: {
    submitted_for_final_approval: { label: 'Resubmit for Approval', variant: 'default' },
    withdrawn: { label: 'Withdraw', variant: 'secondary' },
    cancelled: { label: 'Cancel Review', variant: 'destructive', confirmMessage: 'Cancel this review process?' },
  },
  withdrawn: {
    submitted_for_final_approval: { label: 'Resubmit for Approval', variant: 'default' },
    cancelled: { label: 'Cancel Review', variant: 'destructive', confirmMessage: 'Cancel this review process?' },
  },
  approved: {},
  cancelled: {},
}

const props = defineProps({
  reviewProcessId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: 'Review Process',
  },
  mode: {
    type: String,
    default: 'auto',
    validator(value) {
      return ['auto', 'compact', 'full'].includes(value)
    },
  },
  hiddenWhenInactive: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['updated', 'error'])

const localReviewProcess = ref(null)
const selectedFinalApproverId = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const isUpdatingStatus = ref(false)
const isUpdatingFinalApprover = ref(false)
const users = ref([])
const isLoadingUsers = ref(false)
const hasLoadedUsers = ref(false)
const isRejectModeOpen = ref(false)
const rejectionReason = ref('')
const finalApproverModal = ref(null)

const normalizedReviewProcessId = computed(() => {
  const parsedId = Number(props.reviewProcessId)

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return null
  }

  return parsedId
})

const normalizedStatus = computed(() => {
  if (!localReviewProcess.value) {
    return null
  }

  if (typeof localReviewProcess.value.status === 'string') {
    return localReviewProcess.value.status
  }

  if (localReviewProcess.value.status && typeof localReviewProcess.value.status === 'object' && 'value' in localReviewProcess.value.status) {
    return String(localReviewProcess.value.status.value)
  }

  return null
})

const isActiveReview = computed(() => {
  if (typeof localReviewProcess.value?.is_active === 'boolean') {
    return localReviewProcess.value.is_active
  }

  return normalizedStatus.value === 'submitted_for_final_approval'
})

const isFullMode = computed(() => {
  if (props.mode === 'full') {
    return true
  }

  if (props.mode === 'compact') {
    return false
  }

  return isActiveReview.value
})

const shouldHideCard = computed(() => {
  if (!props.hiddenWhenInactive || props.mode !== 'auto') {
    return false
  }

  return !isActiveReview.value
})

const helperText = computed(() => {
  if (isLoading.value && !localReviewProcess.value) {
    return 'Loading review process details.'
  }

  if (errorMessage.value && !localReviewProcess.value) {
    return 'Unable to load review process.'
  }

  return STATUS_HELPERS[normalizedStatus.value] ?? 'Manage this review process lifecycle.'
})

const statusBadgeLabel = computed(() => {
  if (isLoading.value && !localReviewProcess.value) {
    return 'Loading'
  }

  if (errorMessage.value && !localReviewProcess.value) {
    return 'Unavailable'
  }

  return localReviewProcess.value?.status_label
    ?? STATUS_LABELS[normalizedStatus.value]
    ?? 'Unknown'
})

const statusBadgeVariant = computed(() => {
  if (isLoading.value || (errorMessage.value && !localReviewProcess.value)) {
    return 'outline'
  }

  return STATUS_BADGE_VARIANTS[normalizedStatus.value] ?? 'outline'
})

const transitionOptions = computed(() => {
  const transitions = localReviewProcess.value?.available_status_transitions
    ?? localReviewProcess.value?.availableStatusTransitions
    ?? localReviewProcess.value?.allowed_status_transitions
    ?? localReviewProcess.value?.allowedStatusTransitions

  if (!Array.isArray(transitions)) {
    return []
  }

  return transitions
    .filter((transition) => transition && typeof transition === 'object' && typeof transition.value === 'string')
    .map((transition) => ({
      value: transition.value,
      label: typeof transition.label === 'string' && transition.label.trim() !== ''
        ? transition.label
        : (STATUS_LABELS[transition.value] ?? transition.value),
    }))
})

const actions = computed(() => {
  if (!localReviewProcess.value || !normalizedStatus.value) {
    return []
  }

  const currentStatusActions = STATUS_ACTION_UI[normalizedStatus.value] ?? {}

  return transitionOptions.value.map((transition) => {
    const ui = currentStatusActions[transition.value] ?? {}

    return {
      to: transition.value,
      label: ui.label ?? transition.label ?? transition.value,
      variant: ui.variant ?? 'secondary',
      confirmMessage: ui.confirmMessage ?? null,
      requiresReason: ui.requiresReason ?? false,
    }
  })
})

const primaryReviewAction = computed(() => {
  return actions.value.find((action) => action.to === 'submitted_for_final_approval') ?? null
})

const reviewMenuActions = computed(() => {
  return actions.value.filter((action) => action.to !== primaryReviewAction.value?.to)
})

const hasReviewMenuActions = computed(() => {
  return canUpdateFinalApprover.value || reviewMenuActions.value.length > 0
})

const showActionControls = computed(() => {
  if (!localReviewProcess.value) {
    return false
  }

  if (!isFullMode.value && !primaryReviewAction.value && !hasReviewMenuActions.value) {
    return false
  }

  return primaryReviewAction.value !== null || hasReviewMenuActions.value
})

const canUpdateFinalApprover = computed(() => {
  if (!localReviewProcess.value || !normalizedStatus.value) {
    return false
  }

  return ['not_submitted', 'rejected', 'withdrawn'].includes(normalizedStatus.value)
})

const currentFinalApproverId = computed(() => {
  const localId = Number(localReviewProcess.value?.final_approvable_by_id)

  if (Number.isInteger(localId) && localId > 0) {
    return localId
  }

  const nestedId = Number(localReviewProcess.value?.final_approvable_by?.id ?? localReviewProcess.value?.finalApprovableBy?.id)

  if (Number.isInteger(nestedId) && nestedId > 0) {
    return nestedId
  }

  return null
})

const canSubmitFinalApproverChange = computed(() => {
  if (!Number.isInteger(selectedFinalApproverId.value) || selectedFinalApproverId.value <= 0) {
    return false
  }

  return selectedFinalApproverId.value !== currentFinalApproverId.value
})

const finalApproverDisplay = computed(() => {
  const name = localReviewProcess.value?.final_approvable_by?.name
    ?? localReviewProcess.value?.finalApprovableBy?.name

  if (typeof name === 'string' && name.trim() !== '') {
    return name
  }

  const approverId = currentFinalApproverId.value

  return approverId !== null ? `User #${approverId}` : 'Not assigned'
})

const submittedByDisplay = computed(() => {
  const userName = localReviewProcess.value?.submitted_by_user?.name
    ?? localReviewProcess.value?.submittedByUser?.name
  const submittedAt = formatDateTime(localReviewProcess.value?.submitted_at)

  if (!userName && !submittedAt) {
    return null
  }

  if (!submittedAt) {
    return userName
  }

  if (!userName) {
    return submittedAt
  }

  return `${userName} (${submittedAt})`
})

const finalDecisionByDisplay = computed(() => {
  const userName = localReviewProcess.value?.final_approval_by_user?.name
    ?? localReviewProcess.value?.finalApprovalByUser?.name

  return typeof userName === 'string' && userName.trim() !== ''
    ? userName
    : null
})

const finalDecisionAtDisplay = computed(() => {
  return formatDateTime(localReviewProcess.value?.final_approval_at)
})

const rejectedReason = computed(() => {
  const reason = localReviewProcess.value?.final_approval_rejection_reason

  if (typeof reason !== 'string') {
    return null
  }

  const trimmedReason = reason.trim()

  return trimmedReason === '' ? null : trimmedReason
})

const showRejectedReason = computed(() => {
  return localReviewProcess.value !== null && rejectedReason.value !== null
})

const showCompactStateDetails = computed(() => {
  return localReviewProcess.value !== null && !isFullMode.value
})

const compactSummaryText = computed(() => {
  if (normalizedStatus.value === 'submitted_for_final_approval') {
    return 'Review is currently active.'
  }

  if (normalizedStatus.value === 'approved') {
    return 'Final approval is complete.'
  }

  if (normalizedStatus.value === 'rejected') {
    return 'Review ended with rejection. Address feedback and resubmit.'
  }

  if (normalizedStatus.value === 'withdrawn') {
    return 'Request was withdrawn before final decision.'
  }

  if (normalizedStatus.value === 'cancelled') {
    return 'Review lifecycle was cancelled.'
  }

  return 'Review has not been submitted yet.'
})

const showRejectReasonInput = computed(() => {
  return isRejectModeOpen.value && isFullMode.value
})

watch(
  normalizedReviewProcessId,
  () => {
    fetchReviewProcess()
  },
  { immediate: true }
)

async function fetchReviewProcess() {
  if (normalizedReviewProcessId.value === null) {
    localReviewProcess.value = null
    errorMessage.value = 'Invalid review process id.'
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    const { data } = await axios.get(`/api/v1/review-processes/${normalizedReviewProcessId.value}`)

    patchLocalReviewProcess(data)
  } catch (error) {
    localReviewProcess.value = null
    const message = readErrorMessage(error, 'Unable to load review process.')
    errorMessage.value = message
    emit('error', { error, message })
  } finally {
    isLoading.value = false
  }
}

async function fetchUsers() {
  if (hasLoadedUsers.value || isLoadingUsers.value) {
    return
  }

  isLoadingUsers.value = true

  try {
    const { data } = await axios.get('/api/v1/users', {
      params: {
        not_paginated: true,
        order_by: 'name',
        order_direction: 'asc',
      },
    })

    users.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
    hasLoadedUsers.value = true
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to load users.')
    toast.error(message)
    emit('error', { error, message })
  } finally {
    isLoadingUsers.value = false
  }
}

const updateFinalApprover = async () => {
  if (normalizedReviewProcessId.value === null || !canSubmitFinalApproverChange.value) {
    return false
  }

  isUpdatingFinalApprover.value = true

  try {
    const { data } = await axios.patch(`/api/v1/review-processes/${normalizedReviewProcessId.value}`, {
      final_approvable_by_user_id: selectedFinalApproverId.value,
    })

    patchLocalReviewProcess(data)
    toast.success('Final approver updated.')
    emit('updated', data)
    return true
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to update final approver.')
    toast.error(message)
    emit('error', { error, message })
    return false
  } finally {
    isUpdatingFinalApprover.value = false
  }
}

const onFinalApproverModalShow = async () => {
  selectedFinalApproverId.value = currentFinalApproverId.value
  await fetchUsers()
}

const openFinalApproverModal = () => {
  finalApproverModal.value?.openModal?.()
}

const closeFinalApproverModal = () => {
  finalApproverModal.value?.closeModal?.()
}

const onSelectedFinalApproverIdUpdated = (value) => {
  selectedFinalApproverId.value = value
}

const submitFinalApproverChange = async (event) => {
  event?.preventDefault?.()

  const wasUpdated = await updateFinalApprover()

  if (wasUpdated) {
    closeFinalApproverModal()
  }
}

const runAction = async (action) => {
  if (!localReviewProcess.value || !action) {
    return
  }

  if (action.requiresReason) {
    isRejectModeOpen.value = true
    return
  }

  if (action.confirmMessage && !window.confirm(action.confirmMessage)) {
    return
  }

  await transitionStatus(action.to)
}

const confirmRejectTransition = async () => {
  const normalizedReason = rejectionReason.value.trim()

  if (normalizedReason === '') {
    toast.error('A rejection reason is required.')
    return
  }

  await transitionStatus('rejected', normalizedReason)
}

const cancelRejectTransition = () => {
  isRejectModeOpen.value = false
  rejectionReason.value = ''
}

const transitionStatus = async (status, reason = null) => {
  if (normalizedReviewProcessId.value === null || typeof status !== 'string' || status.trim() === '') {
    return
  }

  isUpdatingStatus.value = true

  try {
    const payload = {
      status,
      ...(reason ? { final_approval_rejection_reason: reason } : {}),
    }

    const { data } = await axios.patch(`/api/v1/review-processes/${normalizedReviewProcessId.value}/status`, payload)

    patchLocalReviewProcess(data)
    isRejectModeOpen.value = false
    rejectionReason.value = ''
    toast.success(`Review status changed to ${STATUS_LABELS[status] ?? status}.`)
    emit('updated', data)
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to update review status.')
    toast.error(message)
    emit('error', { error, message })
  } finally {
    isUpdatingStatus.value = false
  }
}

function patchLocalReviewProcess(reviewProcess) {
  if (!reviewProcess || typeof reviewProcess !== 'object') {
    localReviewProcess.value = null
    selectedFinalApproverId.value = null
    return
  }

  localReviewProcess.value = reviewProcess
  selectedFinalApproverId.value = currentFinalApproverId.value
  errorMessage.value = ''
}

const readErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.errors?.status?.[0]
    || error?.response?.data?.errors?.final_approval_rejection_reason?.[0]
    || error?.response?.data?.errors?.final_approvable_by_user_id?.[0]
    || error?.response?.data?.message
    || fallbackMessage
}

const formatDateTime = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return value
  }

  return parsedDate.toLocaleString()
}
</script>
